import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { courseEvents } from "@/mocks/courseEvents";

interface AlertRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseTitle?: string;
  courseId?: string;
}

export default function AlertRegistrationModal({
  isOpen,
  onClose,
  courseTitle,
  courseId,
}: AlertRegistrationModalProps) {
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setPhone("");
      onClose();
    }, 2000);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] flex items-center justify-center"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="relative w-full max-w-[420px] mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden z-10"
          >
            <div className="bg-[#1C1917] px-6 py-5 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">개강 알림톡 신청</h2>
                <p className="text-xs text-white/50 mt-0.5">
                  개강 시 카카오톡으로 가장 먼저 알려드립니다
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
                aria-label="닫기"
              >
                <i className="ri-close-line text-white"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 md:p-8">
              {courseTitle && (
                <div className="flex items-center gap-2 mb-5 p-3 bg-[#C4956A]/5 rounded-lg">
                  <i className="ri-bookmark-line text-[#C4956A]"></i>
                  <span className="text-sm font-medium text-[#5C5650]">{courseTitle}</span>
                </div>
              )}

              {!submitted ? (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-[#1C1917] mb-1.5">
                      휴대폰번호 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      placeholder="010-0000-0000"
                      className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors placeholder:text-[#B8B3AE]"
                    />
                    <p className="text-[10px] text-[#8C8780] mt-1.5">
                      입력하신 번호로 개강 당일 카카오 알림톡이 발송됩니다
                    </p>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3.5 text-sm font-semibold rounded-full bg-[#C4956A] text-white hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
                  >
                    개강 알림 신청하기
                  </button>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6"
                >
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
                    <i className="ri-check-line text-2xl text-emerald-500"></i>
                  </div>
                  <p className="text-sm font-bold text-[#1C1917] mb-1">알림 신청 완료!</p>
                  <p className="text-xs text-[#8C8780]">개강 당일 알림톡을 보내드립니다</p>
                </motion.div>
              )}
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}